"use client";

import React, { useState, useRef, useEffect } from "react";
import { getModelsByBrandID, getVariantListOnly, searchCarsAndBrands } from "@/app/utilities/apis";
import type { Brand, Model, Variant, SelectedCar } from '@/app/utilities/compareTypes';
import ImageWrapper from "./ImageWrapper";
import Cookies from 'js-cookie';
import Image from "next/image";
import { formatPrice } from "@/app/utilities/helper";

interface CarSelectorProps {
    index: number;
    onClose: () => void;
    onSelect: (index: number, data: SelectedCar, fullVariant: SelectedCar) => void;
    brands: Brand[];
    brandsLoading?: boolean;
    models: Model[];
    setModels: (models: Model[]) => void;
    variants?: Variant[];
    setVariants?: (variants: Variant[]) => void;
    initialSelection?: SelectedCar | null;
    selectedVariantIds?: string[];
}

export default function CarSelector({
    index,
    onClose,
    onSelect,
    brands,
    brandsLoading = false,
    models,
    setModels,
    variants,
    setVariants,
    initialSelection,
    selectedVariantIds,
}: CarSelectorProps) {


    const [step, setStep] = useState<"brand" | "model" | "variant">("brand");
    const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
    const [selectedModel, setSelectedModel] = useState<Model | null>(null);
    const [loading, setLoading] = useState(false);
    const isLoading = loading || (step === "brand" && brandsLoading);
    const [error, setError] = useState<string | null>(null);
    const [duplicateVariantId, setDuplicateVariantId] = useState<string | null>(null);
    const [brandSearch, setBrandSearch] = useState("");
    const [modelSearch, setModelSearch] = useState("");
    const [variantSearch, setVariantSearch] = useState("");
    const [searchResultModels, setSearchResultModels] = useState<Model[]>([]);
    const [searchResultBrands, setSearchResultBrands] = useState<Brand[]>([]);
    const [isSearching, setIsSearching] = useState(false);

    const modalRef = useRef<HTMLDivElement>(null);
    const brandInputRef = useRef<HTMLInputElement | null>(null);
    const modelInputRef = useRef<HTMLInputElement | null>(null);
    const variantInputRef = useRef<HTMLInputElement | null>(null);

    // Close on outside click
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
                onClose();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [onClose]);

    // Load models for selected brand. Comparing against a discontinued model
    // is still useful (e.g. outgoing vs. incoming), so fetch both the normal
    // (active-timing) and discontinued lists and merge them.
    const loadModels = async (brandId: string) => {
        setLoading(true);
        setError(null);
        let result: Model[] = [];
        try {
            const [activeRes, discontinuedRes] = await Promise.all([
                getModelsByBrandID({ brandId, page: 1, limit: 100 }),
                getModelsByBrandID({ brandId, page: 1, limit: 100, is_discontinued: true }),
            ]);
            const activeData = Array.isArray(activeRes) ? activeRes : (activeRes as any).data || [];
            const discontinuedData = Array.isArray(discontinuedRes) ? discontinuedRes : (discontinuedRes as any).data || [];
            const data = [...activeData, ...discontinuedData];
            setModels(data);
            if (data.length === 0) setError("No cars available for this brand at the moment. We're constantly updating our listings — check back soon!");
            result = data as Model[];
        } catch {
            setError("Failed to load models");
            result = [];
        } finally {
            setLoading(false);
        }
        return result;
    };

    // Load variants for selected model
    const loadVariants = async (modelId: string) => {
        setLoading(true);
        setError(null);
        let result: Variant[] = [];
        try {
            const res = await getVariantListOnly(modelId, 1, 100);
            const data = Array.isArray(res) ? res : (res as any).data || [];
            setVariants?.(data as Variant[]);
            if (data.length === 0) setError("No variants listed for this model at the moment. New variants are added regularly — check back soon!");
            result = data as Variant[];
        } catch {
            setError("Failed to load variants");
            result = [];
        } finally {
            setLoading(false);
        }
        return result;
    };

    // Dynamic heading
    const getHeading = () => {
        if (step === "brand") return "Select Brand";
        if (step === "model") return `Select ${selectedBrand?.name || ""} Model`;
        return `Select ${selectedModel?.name || ""} Variant`;
    };

    // Navigate via breadcrumbs
    const goToStep = async (target: "brand" | "model") => {
        if (target === "brand") {
            setStep("brand");
            setSelectedBrand(null);
            setSelectedModel(null);
            setVariants?.([]);
            setModels([]);
            setError(null);
            // focus brand input after render
            setTimeout(() => brandInputRef.current?.focus(), 50);
        } else if (target === "model" && selectedBrand) {
            // Ensure we show the full, up-to-date models list for the selected brand
            setStep("model");
            setSelectedModel(null);
            setVariants?.([]);
            setModelSearch("");
            // reload models for the brand so the user gets all models when clicking the breadcrumb
            await loadModels(selectedBrand._id);
            // focus model input after models are loaded and rendered
            setTimeout(() => modelInputRef.current?.focus(), 50);
        }
    };

    // Select model → go to variants
    const handleModelSelect = async (model: Model) => {
        setSelectedModel(model);
        setStep("variant");
        await loadVariants(model._id);
        // focus variant search after variants are loaded
        setTimeout(() => variantInputRef.current?.focus(), 50);
    };

    // Select variant → finalize selection
    const handleVariantSelect = async (variant: Variant) => {

        // If variant is already chosen in another slot, block and show alternatives
        const isTaken = Boolean(
            selectedVariantIds &&
            variant &&
            variant._id &&
            selectedVariantIds.some((id) => String(id) === String(variant._id))
        );
        if (isTaken) {
            setDuplicateVariantId(variant._id);
            setError("You can't compare the same vehicle. Please select another variant.");
            return;
        }
        const selected: SelectedCar = {
            _id: variant._id,
            brand_name: selectedModel?.brand_name ?? '',
            brand_slug: selectedModel?.brand_slug ?? '',
            model_name: selectedModel?.name ?? '',
            model_slug: selectedModel?.slug ?? '',
            detailed_slug: selectedModel?.detailed_slug ?? '',
            featured_image: selectedModel?.featured_image ?? '',
            variant_name: variant.name,
            variant_slug: variant.slug,
            price: variant.price,
            is_discontinued: variant.status === 'discontinued' || !!variant.is_discontinued,
            is_upcoming: variant.status === 'upcoming' || !!variant.is_upcoming,
            fuel: variant?.fuel_types?.length ? variant?.fuel_types[0] : "",
            transmission: variant?.transmission ?? "",
            specSections: variant?.specSections || [],
        };

        try {
            let selectedVariants: string[] = [];

            const raw = Cookies.get('selected_variant_slugs');

            if (raw) {
                try {
                    const parsed = JSON.parse(raw);
                    if (Array.isArray(parsed)) {
                        selectedVariants = parsed;
                    }
                } catch {
                    console.warn("Invalid cookie data, resetting.");
                }
            }

            // Guard
            if (!selected.detailed_slug || !selected.variant_slug) {
                console.warn("Missing detailed_slug or variant_slug");
                return;
            }

            const key = `${selected.detailed_slug}+${selected.variant_slug}`;

            // ✅ Avoid duplicates
            if (!selectedVariants.includes(key)) {
                selectedVariants.push(key);
            }

            Cookies.set(
                'selected_variant_slugs',
                JSON.stringify(selectedVariants),
                {
                    path: '/',
                    sameSite: 'lax',
                    expires: 7
                }
            );

        } catch (error) {
            console.error("Failed to save selected variant:", error);
        }


        // clear duplicate marker
        setDuplicateVariantId(null);
        setError(null);
        onSelect(index, selected, selected);
        onClose();
    };

    // Handle model selection directly from the brand stage search results
    const handleModelSelectFromBrandStage = async (model: any) => {
        const bId = typeof model.brand_id === "object" ? model.brand_id._id : model.brand_id;
        const bName = model.brand_name || (typeof model.brand_id === "object" ? model.brand_id.name : "");
        const bSlug = model.brand_slug || (typeof model.brand_id === "object" ? model.brand_id.slug : "");
        const mName = model.name || model.title || model.display_name || "";
        const mSlug = model.slug || model.model_slug || "";
        const mDetailedSlug = model.detailed_slug || mSlug;
        const mImage = model.featured_image || model.image || "";

        const brandObj = brands.find((b) => b._id === bId) || {
            _id: bId,
            name: bName,
            slug: bSlug,
            image: "",
        };

        const normalizedModel: Model = {
            _id: model._id,
            name: mName,
            slug: mSlug,
            detailed_slug: mDetailedSlug,
            brand_id: bId,
            brand_name: bName,
            brand_slug: bSlug,
            featured_image: mImage,
        };

        setSelectedBrand(brandObj as Brand);
        setSelectedModel(normalizedModel);
        setStep("variant");
        await loadVariants(model._id);
        setTimeout(() => variantInputRef.current?.focus(), 50);
    };

    // Combined search for brands and models
    useEffect(() => {
        if (step === "brand" && brandSearch.trim().length >= 2) {
            setIsSearching(true);
            const delayDebounceFn = setTimeout(async () => {
                try {
                    const { brandsList, productList } = await searchCarsAndBrands(brandSearch);
                    setSearchResultBrands(brandsList);
                    setSearchResultModels(productList);
                } catch (err) {
                    console.error("Search failed:", err);
                } finally {
                    setIsSearching(false);
                }
            }, 300);
            return () => clearTimeout(delayDebounceFn);
        } else {
            setSearchResultModels([]);
            setSearchResultBrands([]);
            setIsSearching(false);
        }
    }, [brandSearch, step]);

    // Prefill when editing an existing selection
    useEffect(() => {
        let mounted = true;
        const prefill = async () => {
            if (!initialSelection) return;

            // find brand from brands list
            const brand = brands.find((b) => b.slug === initialSelection.brand_slug);
            if (brand) {
                setSelectedBrand(brand);
                setStep("model");
                // load models and try to set selected model
                const models = await loadModels(brand._id);
                if (!mounted) return;
                const model = models.find((m) => m.slug === initialSelection.model_slug);
                if (model) {
                    setSelectedModel(model);
                    setStep("variant");
                    await loadVariants(model._id);
                }
            }
        };

        prefill();

        return () => {
            mounted = false;
        };
    }, [initialSelection]);

    // Always focus the appropriate search input when step changes or modal mounts
    useEffect(() => {
        try {
            if (step === "brand") {
                brandInputRef.current?.focus();
                brandInputRef.current?.select();
            } else if (step === "model") {
                modelInputRef.current?.focus();
                modelInputRef.current?.select();
            } else if (step === "variant") {
                variantInputRef.current?.focus();
                variantInputRef.current?.select();
            }
        } catch (e) {
            // ignore
        }
    }, [step]);

    // NOTE: Removed comparable-flag filtering — show brands based on search only
    const filteredBrands = brands.filter((b) =>
        b.name.toLowerCase().includes(brandSearch.toLowerCase())
    );

    const isBrandActive = (b: any) => {
        return (
            b?.is_active === true ||
            b?.isActive === true ||
            b?.active === true ||
            // if no explicit flag present, treat as active to avoid hiding brands unexpectedly
            (b?.is_active === undefined && b?.isActive === undefined && b?.active === undefined)
        );
    };

    const isBrandPopular = (b: any) =>
        b?.is_popular === true || b?.isPopular === true || b?.popular === true;

    // Only include active brands in the lists
    const activeFilteredBrands = filteredBrands.filter((b) => isBrandActive(b));

    const popularBrands = activeFilteredBrands.filter((b) => isBrandPopular(b));

    // Sort helpers: present lists alphabetically by name
    const sortByName = (arr: typeof brands) =>
        [...arr].sort((a, b) => (a?.name || "").localeCompare(b?.name || ""));

    const activeFilteredBrandsSorted = sortByName(activeFilteredBrands);
    const popularBrandsSorted = sortByName(popularBrands);
    const AllBrandsSorted = sortByName(activeFilteredBrands);

    const filteredModels = models.filter((m) =>
        m.name.toLowerCase().includes(modelSearch.toLowerCase())
    );

    const filteredVariants = (variants || []).filter((v) =>
        v.name.toLowerCase().includes(variantSearch.toLowerCase())
    );


    return (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto">
            <div ref={modalRef} className="bg-white rounded-lg shadow-xl w-full max-w-lg border border-cyan-500 h-[500px] flex flex-col">
                {/* Header */}
                <div className="p-6 pb-3">
                    <div className="flex justify-between items-center mb-2">
                        <h2 className="text-xl font-bold text-gray-900">{getHeading()}</h2>
                    </div>

                    {/* Breadcrumbs */}
                    <div className="flex items-center text-sm text-gray-600">
                        {/* Brand */}
                        <button onClick={() => goToStep("brand")} className="flex items-center gap-1 font-medium text-green-600 hover:text-cyan-600 transition cursor-pointer" >
                            {step !== "brand" && (
                                <Image
                                    src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}right-arrow.svg`}
                                    alt="check"
                                    width={14}
                                    height={14}
                                    className="inline-block mr-1"
                                    style={{
                                    filter:
                                        "invert(48%) sepia(77%) saturate(378%) hue-rotate(85deg) brightness(95%) contrast(92%)"
                                    }}
                                />
                                )}
                            Brand
                        </button>
                        <Image
                            src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}lib-icon/ChevronRight.svg`} 
                            alt="search"
                            width={8} 
                            height={8}
                            className="w-[8px] h-[8px] invert-[60%] brightness-[70%] mx-1"
                            title="ChevronRight Next"
                        />

                        {/* Model */}
                        <button
                            onClick={() => selectedBrand && goToStep("model")}
                            disabled={!selectedBrand}
                            className={`flex items-center gap-1 font-medium transition cursor-pointer ${step === "model" || step === "variant" ? "text-green-600 hover:text-cyan-600" : "text-gray-400 cursor-not-allowed"}`}>
                            {step === "variant" && (
                            <Image
                                src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}right-arrow.svg`}
                                alt="check"
                                width={14}
                                height={14}
                                className="inline-block"
                                style={{
                                filter:
                                    "invert(48%) sepia(77%) saturate(378%) hue-rotate(85deg) brightness(95%) contrast(92%)"
                                }}
                            />
                            )}
                            Model
                        </button>
                        <Image
                            src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}lib-icon/ChevronRight.svg`} 
                            alt="ChevronRight Next"
                            title="ChevronRight Next"
                            width={8} 
                            height={8}
                            className="w-[8px] h-[8px] invert-[60%] brightness-[70%] mx-1"
                        />

                        {/* Variant */}
                        <span className={`flex items-center gap-1 font-medium ${step === "variant" ? "text-green-600" : "text-gray-400"}`}>
                            Variant
                        </span>
                    </div>
                </div>

                <hr className="border-gray-200" />

                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto p-2">
                    {error && (
                        <div className="mx-4 my-2 p-3 rounded-md border border-red-100 bg-red-50 text-red-800">
                            <div className="flex items-start gap-2">
                                <div className="flex-shrink-0 mt-0.5">
                                    <Image
                                        src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}lib-icon/cross-icon-new.svg`} 
                                        alt="search"
                                        title="search"
                                        width={16} 
                                        height={16}
                                        className="w-[16px] h-[16px] invert-[45%] brightness-[70%]"
                                    />
                                </div>
                                <div className="flex-1 text-sm">
                                    {duplicateVariantId ? (
                                        <>
                                            <div className="font-semibold text-red-800 text-sm">Looks like you selected the same car twice.</div>
                                            {variants && variants.length > 0 ? (
                                                <div className="mt-2">
                                                    <div className="text-xs font-medium mb-2">Please pick a different variants to continue.</div>
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                                        {variants
                                                            .filter((v) => v._id !== duplicateVariantId)
                                                            .map((v) => (
                                                                <button
                                                                    key={v._id}
                                                                    onClick={() => {
                                                                        setError(null);
                                                                        setDuplicateVariantId(null);
                                                                        handleVariantSelect(v);
                                                                    }}
                                                                    className="w-full text-left p-2 border border-red-50 rounded bg-white hover:bg-red-25 transition text-sm"
                                                                >
                                                                    <div className="flex justify-between items-center">
                                                                        <div className="font-medium text-sm">{selectedModel?.name} {v.name}</div>
                                                                        <div className="text-xs text-cyan-600">₹{(v.price / 100000).toFixed(2)} L</div>
                                                                    </div>
                                                                </button>
                                                            ))
                                                        }
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="mt-2 text-xs text-gray-600">No other variants available.</div>
                                            )}
                                        </>
                                    ) : (
                                        <div className="font-semibold text-red-800 text-sm">{error}</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                    {isLoading && (
                        <div className="flex justify-center py-8">
                            <Image
                                src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}lib-icon/Loader2.svg`}
                                alt="loader"
                                width={24}
                                height={24}
                                className="animate-spin"
                                />
                        </div>
                    )}

                    {/* === BRAND STEP === */}
                    {step === "brand" && !isLoading && !error && (
                        <>
                            <div className="py-4">
                                <div className="relative">
                                    <Image
                                        src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}lib-icon/search.svg`} 
                                        alt="search"
                                        title="search"
                                        width={18} 
                                        height={18}
                                        className="absolute left-3 top-2 text-[#99A1AF] w-[18px] h-[18px] z-10 invert-[60%] brightness-[70%]"
                                    />
                                    <input
                                        ref={brandInputRef}
                                        type="text"
                                        value={brandSearch}
                                        onChange={(e) => setBrandSearch(e.target.value)}
                                        placeholder="Search brands..."
                                        className="w-full pl-10 pr-4 py-2 bg-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm"
                                    />
                                </div>
                            </div>

                            {/* When searching (2+ chars), show API results.
                                Otherwise show popular/all brands. */}
                            {brandSearch.trim().length >= 2 ? (
                                <div className="p-4 pt-0">
                                    <div className="space-y-1 max-h-64 overflow-y-auto">
                                        {searchResultBrands.length > 0 && (
                                            <div className="mb-2">
                                                {searchResultModels.length > 0 && (
                                                    <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1 px-3">Brands</div>
                                                )}
                                                {searchResultBrands.map((brand) => (
                                                    <div
                                                        key={brand._id}
                                                        onClick={() => {
                                                            setSelectedBrand(brand);
                                                            setStep("model");
                                                            setModelSearch("");
                                                            loadModels(brand._id).then(() => setTimeout(() => modelInputRef.current?.focus(), 50));
                                                        }}
                                                        className="w-full flex items-center gap-3 px-3 py-2 rounded hover:bg-cyan-50 cursor-pointer"
                                                    >
                                                        <span className="text-sm font-medium text-gray-800">{brand.name}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {searchResultModels.length > 0 && (
                                            <div className="mb-2">
                                                <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1 px-3">Models</div>
                                                {searchResultModels.map((model) => (
                                                    <div
                                                        key={model._id}
                                                        onClick={() => handleModelSelectFromBrandStage(model)}
                                                        className="w-full flex items-center gap-3 px-3 py-2 rounded hover:bg-cyan-50 cursor-pointer"
                                                    >
                                                        <span className="text-sm font-medium text-gray-800">
                                                            {model.brand_name || (typeof model.brand_id === 'object' ? (model.brand_id as any).name : "")} {model.name}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {isSearching && (
                                            <div className="flex justify-center py-6">
                                                <Image
                                                    src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}lib-icon/Loader2.svg`}
                                                    alt="loader"
                                                    width={24}
                                                    height={24}
                                                    className="animate-spin"                                        
                                                    />
                                            </div>
                                        )}

                                        {!isSearching && searchResultBrands.length === 0 && searchResultModels.length === 0 && (
                                            <p className="text-center text-gray-500 py-6 text-sm">No results found</p>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {popularBrandsSorted.length > 0 && (
                                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 pt-0">
                                            {popularBrandsSorted.map((brand) => (
                                                <div
                                                    key={brand._id}
                                                    onClick={() => {
                                                        setSelectedBrand(brand);
                                                        setStep("model");
                                                        setModelSearch("");
                                                        loadModels(brand._id).then(() => setTimeout(() => modelInputRef.current?.focus(), 50));
                                                    }}
                                                    className="flex flex-col items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition group"
                                                    data-testid={`compare-popup-brand`}
                                                >
                                                    <div className="relative">
                                                        <ImageWrapper
                                                            src={brand.image}
                                                            alt={brand.name}
                                                            height={64}
                                                            width={100}
                                                            className="object-contain"
                                                            onError={(e) => {
                                                                e.currentTarget.src = `${process.env.NEXT_PUBLIC_CDN_URL}placeholder-car.webp`;
                                                            }}
                                                        />
                                                    </div>
                                                    <p className="text-[10px] md:text-[13px] text-center whitespace-normal w-full mt-1 font-medium">
                                                        {brand.name}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {AllBrandsSorted.length > 0 && (
                                        <div className="py-4 px-2 pt-0">
                                            <div className="text-sm text-gray-600 py-2 mb-2 font-semibold mt-2 border-b border-gray-200">All Brands</div>
                                            <div className="max-h-64 overflow-y-auto mt-3">
                                                {AllBrandsSorted.map((brand) => (
                                                    <div
                                                        key={brand._id}
                                                        onClick={() => {
                                                            setSelectedBrand(brand);
                                                            setStep("model");
                                                            setModelSearch("");
                                                            loadModels(brand._id).then(() => setTimeout(() => modelInputRef.current?.focus(), 50));
                                                        }}
                                                        className="w-full flex items-center gap-3 px-1 py-3 rounded hover:bg-cyan-50 cursor-pointer border-b border-gray-100"
                                                    >
                                                        <span className="text-[11px] md:text-sm font-medium text-gray-800">{brand.name}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {popularBrandsSorted.length === 0 && AllBrandsSorted.length === 0 && (
                                        <p className="text-center text-gray-500 py-6 text-sm">No brands found</p>
                                    )}
                                </>
                            )}
                        </>
                    )}

                    {/* === MODEL STEP === */}
                    {step === "model" && !loading && !error && (
                        <div className="px-1 py-4">
                            <div className="relative mb-3">
                                <Image
                                    src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}lib-icon/search.svg`} 
                                    alt="search"
                                    title="search"
                                    width={18} 
                                    height={18}
                                    className="absolute left-3 top-2 text-[#99A1AF] w-[18px] h-[18px] z-10 invert-[60%] brightness-[70%]"
                                />
                                <input
                                    ref={modelInputRef}
                                    type="text"
                                    value={modelSearch}
                                    onChange={(e) => setModelSearch(e.target.value)}
                                    placeholder="Search models..."
                                    className="w-full pl-10 pr-4 py-2 bg-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm"
                                />
                            </div>

                            <div className="max-h-80 overflow-y-auto">
                                {filteredModels.length > 0 ? (
                                    filteredModels.map((model) => (
                                        <div
                                            key={model._id}
                                            onClick={() => handleModelSelect(model)}
                                            className="px-2 py-3 rounded-md hover:bg-cyan-50 cursor-pointer font-medium text-sm flex items-center justify-between gap-3 transition border-b border-gray-100"
                                            data-testid={`compare-popup-model`}
                                        >
                                            <span>{model.name}</span>
                                            {model.status === "discontinued" && (
                                                <span className="text-[10px] md:text-xs font-medium text-red-500 border border-red-300 bg-red-50 rounded px-2 py-0.5">
                                                    Discontinued
                                                </span>
                                            )}
                                        </div>
                                    ))
                                ) : (
                                    <p className="text-center text-gray-500 py-6 text-sm">
                                        There is no model mapped to this brand.
                                    </p>
                                )}
                            </div>
                        </div>
                    )}

                    {/* === VARIANT STEP === */}
                    {step === "variant" && !loading && !error && (
                        <div className="px-1 py-4">
                            <div className="relative mb-3">
                                <Image
                                    src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}lib-icon/search.svg`} 
                                    alt="search"
                                    title="search"
                                    width={18} 
                                    height={18}
                                    className="absolute left-3 top-2 text-[#99A1AF] w-[18px] h-[18px] z-10 invert-[60%] brightness-[70%]"
                                />
                                <input
                                    ref={variantInputRef}
                                    type="Text"
                                    value={variantSearch}
                                    onChange={(e) => setVariantSearch(e.target.value)}
                                    placeholder="Search variants..."
                                    className="w-full pl-10 pr-4 py-2 bg-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm"
                                />
                            </div>

                            <div className="max-h-80 overflow-y-auto">
                                {filteredVariants.length > 0 ? (
                                    filteredVariants.map((variant) => (
                                        <div
                                            key={variant._id}
                                            onClick={() => handleVariantSelect(variant)}
                                            className="px-2 py-3 rounded-md hover:bg-cyan-50 cursor-pointer font-medium text-sm gap-3 transition border-b border-gray-100"
                                            data-testid={`compare-popup-variant`}
                                        >
                                            <div className="flex justify-between items-center">
                                                <span className="font-medium text-sm text-gray-800">
                                                    {selectedModel?.name} {variant.name}
                                                </span>
                                                <span className="text-cyan-600 font-semibold text-[12px]">
                                                    {formatPrice(variant.price)}
                                                </span>
                                            </div>
                                            {/* <div className="text-xs text-gray-500 mt-1">
                                                {variant.fuel_type} •{" " }{variant.transmission || variant.body_type}
                                            </div> */}
                                        </div>
                                    ))
                                ) : (
                                    <p className="text-center text-gray-500 py-6 text-sm">
                                        No variants listed for this model at the moment. New variants are added regularly — check back soon!
                                    </p>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}